import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { isAdminUser } from "@/lib/auth";

import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import DashboardPage from "./pages/DashboardPage";
import AnalysisPage from "./pages/AnalysisPage.tsx";
import AssistantPage from "./pages/AssistantPage";
import PricingPage from "./pages/PricingPage";
import LibraryPage from "./pages/LibraryPage";
import SetlistsPage from "./pages/SetlistsPage";
import SetlistDetailPage from "./pages/SetlistDetailPage";
import StagePage from "./pages/StagePage";
import RehearsalPage from "./pages/RehearsalPage";
import RecordPage from "./pages/RecordPage";
import ClipsPage from "./pages/ClipsPage";
import TutorialsPage from "./pages/TutorialsPage";
import MediaImportPage from "./pages/MediaImportPage";
import SongDownloadPage from "./pages/SongDownloadPage";
import StemMixerPage from './pages/StemMixerPage';
import PitchTempoPage from './pages/PitchTempoPage';
import VideoAudioPage from './pages/VideoAudioPage';
import CreatorStudioPage from './pages/CreatorStudioPage';
import PublicToolsLayout from './components/audio-tools/PublicToolsLayout';
import YouTubePracticeSyncPage from "./pages/YouTubePracticeSyncPage";
import AdminMusicCatalogPage from "./pages/AdminMusicCatalogPage";
import AdminSongEditorPage from "./pages/AdminSongEditorPage";
import AdminBulkImportPage from "./pages/AdminBulkImportPage";
import AdminArtistImportPage from "./pages/AdminArtistImportPage";

const ProtectedRoute = ({ children, shell = true }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return shell ? <AppShell>{children}</AppShell> : children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const PracticeRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return user ? (
    <AppShell>{children}</AppShell>
  ) : (
    <div className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.16),transparent_32rem),radial-gradient(circle_at_80%_0%,rgba(139,92,246,0.18),transparent_28rem),#05070c] text-slate-100">
      <main className="mx-auto min-w-0 max-w-[1540px] px-3 py-4 sm:px-5 md:px-6 lg:px-8 lg:py-8">
        {children}
      </main>
    </div>
  );
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;
  if (!isAdminUser(user)) {
    console.warn("[AdminRoute] blocked", {
      email: user.email,
      role: user.role,
      is_admin: user.is_admin,
      isAdmin: user.isAdmin,
      condition: "role === 'admin' || is_admin === true || isAdmin === true",
    });
    return <Navigate to="/dashboard" replace />;
  }
  console.info("[AdminRoute] allowed", {
    email: user.email,
    role: user.role,
    is_admin: user.is_admin,
  });

  return <AppShell>{children}</AppShell>;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
          <Route path="/pricing" element={<PricingPage />} />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/analysis/:id" element={<ProtectedRoute><AnalysisPage /></ProtectedRoute>} />
          <Route path="/assistant" element={<ProtectedRoute><AssistantPage /></ProtectedRoute>} />
          
          {/* Library & Setlists */}
          <Route path="/library" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />
          <Route path="/setlists" element={<ProtectedRoute><SetlistsPage /></ProtectedRoute>} />
          <Route path="/setlists/:id" element={<ProtectedRoute><SetlistDetailPage /></ProtectedRoute>} />
          <Route path="/stage/:id" element={<ProtectedRoute><StagePage /></ProtectedRoute>} />
          <Route path="/rehearsal/:id" element={<ProtectedRoute><RehearsalPage /></ProtectedRoute>} />
          
          {/* New Features */}
          <Route path="/record" element={<ProtectedRoute><RecordPage /></ProtectedRoute>} />
          <Route path="/clips" element={<ProtectedRoute><ClipsPage /></ProtectedRoute>} />
          <Route path="/tutorials" element={<ProtectedRoute><TutorialsPage /></ProtectedRoute>} />
          <Route path="/youtube-practice" element={<PracticeRoute><YouTubePracticeSyncPage /></PracticeRoute>} />
          <Route path="/youtube-practice/:sourceId" element={<PracticeRoute><YouTubePracticeSyncPage /></PracticeRoute>} />
          <Route path="/media-import" element={<ProtectedRoute><MediaImportPage /></ProtectedRoute>} />
          <Route path="/download-songs" element={<PracticeRoute><SongDownloadPage /></PracticeRoute>} />
          <Route path="/stem-mixer/:analysisId" element={<ProtectedRoute><StemMixerPage /></ProtectedRoute>} />
          <Route path="/pitch-tempo" element={<PublicToolsLayout><PitchTempoPage /></PublicToolsLayout>} />
          <Route path="/video-audio" element={<PublicToolsLayout><VideoAudioPage /></PublicToolsLayout>} />
          <Route path="/creator-studio" element={<CreatorStudioPage />} />

          {/* Administrator Music Catalog */}
          <Route path="/admin" element={<Navigate to="/admin/music-catalog" replace />} />
          <Route path="/admin/music-catalog" element={<AdminRoute><AdminMusicCatalogPage section="catalog" /></AdminRoute>} />
          <Route path="/admin/music-catalog/new" element={<AdminRoute><AdminSongEditorPage create /></AdminRoute>} />
          <Route path="/admin/music-catalog/:songId" element={<AdminRoute><AdminSongEditorPage /></AdminRoute>} />
          <Route path="/admin/music-catalog/:songId/edit" element={<AdminRoute><AdminSongEditorPage /></AdminRoute>} />
          <Route path="/admin/review" element={<AdminRoute><AdminMusicCatalogPage section="review" /></AdminRoute>} />
          <Route path="/admin/audit-log" element={<AdminRoute><AdminMusicCatalogPage section="audit" /></AdminRoute>} />
          <Route path="/admin/bulk-import" element={<AdminRoute><AdminBulkImportPage /></AdminRoute>} />
          <Route path="/admin/artist-import" element={<AdminRoute><AdminArtistImportPage /></AdminRoute>} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
